import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import RedoIcon from '@mui/icons-material/Redo';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SettingsIcon from '@mui/icons-material/Settings';
import UndoIcon from '@mui/icons-material/Undo';
import YoutubeSearchedForIcon from '@mui/icons-material/YoutubeSearchedFor';
import Box from '@mui/material/Box';
import { darken, lighten } from '@mui/material/styles';
import { viewportHeight } from 'common/utilities/browser/viewport/viewport';
import { arrayFlat, arrayUnique } from 'common/utilities/core/array';
import { getTextWidth } from 'common/utilities/core/string';
import { calcPageMarginTop } from 'common/utilities/hooks/usePageMarginTop/usePageMarginTop';
import { isItemWithValue, isMultiUnitValuesItem } from 'core/api/api.typesGuards';
import type * as I from 'core/api/coreApiClient';
import type { AppDispatch } from 'core/redux/store/configureStore.types';
import type { EnhancedCommand } from 'core/utilities/core.types';
import { sectionContentMaxHeight } from 'core/utilities/section/section';
import theme, { ThemeVars } from 'core/utilities/theme';
import deepmerge from 'deepmerge';
import type { NetworkData, NodePositionWithId } from 'entityHierarchy/components/EntityHierarchyVisualization/EntityHierarchyVisualization';
import I18n from 'entityHierarchy/i18n/en-US';
import type {   EntityHierarchyState,   EntityHierarchyViewSettings} from 'entityHierarchy/redux/reducers/entityHierarchyReducer';
import {
  changeClearScale,
  changeView, download, EntityHierarchyDownload,
EntityHierarchyView,
  Orientation,
  processChangePositionsAction,
toggleModal,
  toggleViewSettingsModal,
} from 'entityHierarchy/redux/reducers/entityHierarchyReducer';
import React from 'react';
import isEqual from 'react-fast-compare';
import type { Edge, Node, Options } from 'vis-network';

/** Color is used when there is only one type of entity in a hierarchy */
export const defaultEntityTypeColor = theme.palette.primary.main;

export function isEntityHierarchyAvailable(entityHierarchy?: I.EntityHierarchy) {
  return entityHierarchy !== undefined && entityHierarchy.nodes.length > 1;
}

export function entityHierarchySectionCommands(
  sectionId: string,
  entityHierarchy: I.EntityHierarchy | undefined,
  dispatch: AppDispatch,
  isFullscreen: boolean,
  entityHierarchyState: Partial<EntityHierarchyState>,
  extendedView: boolean,
): EnhancedCommand[] {
  const onlyFullTreeView = sectionId === 'EntityHierarchyMulti__pds';

  const active = isEntityHierarchyAvailable(entityHierarchy);

  const commands: EnhancedCommand[] = [
    {
      id: 'treeView',
      title: I18n.utils.entityHierarchySectionCommands.treeView.all,
      label: 'Tree view',
      onClick: (command: EnhancedCommand) => {
        dispatch(changeView({ id: sectionId, data: command.id as unknown as EntityHierarchyView }));
      },
      active,
      small: true,
      commands: [
        {
          title: I18n.utils.entityHierarchySectionCommands.treeView.allTitle,
          label: I18n.utils.entityHierarchySectionCommands.treeView.all,
          active,
          commands: [],
          id: EntityHierarchyView.all,
        },
        {
          title: I18n.utils.entityHierarchySectionCommands.treeView.directParentTitle,
          label: I18n.utils.entityHierarchySectionCommands.treeView.directParent,
          active: !onlyFullTreeView,
          commands: [],
          id: EntityHierarchyView.directAscendant,
        },
        {
          title: I18n.utils.entityHierarchySectionCommands.treeView.parentTitle,
          label: I18n.utils.entityHierarchySectionCommands.treeView.parent,
          active: !onlyFullTreeView,
          commands: [],
          id: EntityHierarchyView.ascendant,
        },
        {
          title: I18n.utils.entityHierarchySectionCommands.treeView.childTitle,
          label: I18n.utils.entityHierarchySectionCommands.treeView.child,
          active: !onlyFullTreeView,
          commands: [],
          id: EntityHierarchyView.descendant,
        },
      ],
      toggleButton: { variant: 'dropdown', selected: entityHierarchyState.view || EntityHierarchyView.all },
    },
    {
      id: 'viewSettings',
      title: I18n.utils.entityHierarchySectionCommands.viewSettingsTitle,
      iconEl: <Box sx={{ display: 'flex', mr: -0.5 }}><SettingsIcon /></Box>,
      onClick: () => {
        dispatch(toggleViewSettingsModal({ id: sectionId, data: true }));
      },
      active,
      commands: [],
      small: true,
      hideArrowIcon: true,
    },
    {
      id: 'clearZoom',
      title: I18n.utils.entityHierarchySectionCommands.clearZoomTitle,
      iconEl: <Box sx={{ display: 'flex', mr: -0.5 }}><YoutubeSearchedForIcon /></Box>,
      onClick: () => {
        dispatch(changeClearScale({ id: sectionId, data: true }));
      },
      active,
      commands: [],
      small: true,
    },
    {
      id: 'clearChangedPositions',
      title: I18n.utils.entityHierarchySectionCommands.clearChangedPositions,
      iconEl: <Box sx={{ display: 'flex', mr: -0.5 }}><RestartAltIcon /></Box>,
      onClick: () => {
        dispatch(processChangePositionsAction({
          id: sectionId,
          data: { action: 'clear', canUndo: false, canRedo: false },
        }));
      },
      active,
      commands: [],
      small: true,
    },

    {
      id: 'undo',
      title: I18n.utils.entityHierarchySectionCommands.undoTitle,
      iconEl: <Box sx={{ display: 'flex', mr: -0.5 }}><UndoIcon /></Box>,
      onClick: () => {
        dispatch(processChangePositionsAction({
          id: sectionId,
          data: { action: 'undo', canUndo: false, canRedo: false },
        }));
      },
      active: entityHierarchyState.canUndo,
      commands: [],
      small: true,
    },
    {
      id: 'redo',
      title: I18n.utils.entityHierarchySectionCommands.redoTitle,
      iconEl: <Box sx={{ display: 'flex', mr: -0.5 }}><RedoIcon /></Box>,
      onClick: () => {
        dispatch(processChangePositionsAction({
          id: sectionId,
          data: { action: 'redo', canUndo: false, canRedo: false },
        }));
      },
      active: entityHierarchyState.canRedo,
      commands: [],
      small: true,
    },
    {
      id: 'download',
      title: 'Download',
      active,
      styleHint: 'export',
      commands: [
        {
          title: I18n.utils.entityHierarchySectionCommands.downloadPartialTitle,
          label: I18n.utils.entityHierarchySectionCommands.downloadPartial,
          onClick: () => {
            dispatch(download({
              id: sectionId,
              data: isFullscreen ? EntityHierarchyDownload.partialFullscreen : EntityHierarchyDownload.partial,
            }));
          },
          active,
          commands: [],
          id: 'downloadPartial',
        },
        {
          title: I18n.utils.entityHierarchySectionCommands.downloadFullTitle,
          label: I18n.utils.entityHierarchySectionCommands.downloadFull,
          onClick: () => {
            dispatch(download({
              id: sectionId,
              data: EntityHierarchyDownload.full,
            }));
          },
          active,
          commands: [],
          id: 'downloadFull',
        },
      ],
      small: true,
    },
  ];

  if (isFullscreen) {
    commands.push({
      id: 'exitFullscreen',
      title: I18n.utils.entityHierarchySectionCommands.exitFullscreenTitle,
      iconEl: <Box sx={{ display: 'flex', mr: -0.5 }}><FullscreenExitIcon /></Box>,
      onClick: () => {
        dispatch(toggleModal({ id: sectionId, data: false }));
      },
      shortcut: ['Escape', false],
      active,
      commands: [],
      small: true,
      hideArrowIcon: true,
    });
  } else {
    commands.push({
      id: 'fullscreen',
      title: I18n.utils.entityHierarchySectionCommands.fullscreenTitle,
      iconEl: <Box sx={{ display: 'flex', mr: -0.5 }}><FullscreenIcon /></Box>,
      onClick: () => {
        dispatch(toggleModal({ id: sectionId, data: true }));
      },
      active,
      commands: [],
      small: true,
      hideArrowIcon: true,
    });
  }

  if (!extendedView) {
    return commands.filter((c) => (
      ['treeView', 'clearZoom', 'clearChangedPositions', 'download'].includes(c.id)
    ));
  }

  return commands;
}

export function entityEdgeChildren(currentEdges: I.EntityEdge[], edges: I.EntityEdge[]): I.EntityEdge[] {
  if (currentEdges.length === 0) return []; // no child

  const currentEdgesToNodeIds = currentEdges.map((e) => e.toNodeId);
  const directChildren = edges.filter((e) => currentEdgesToNodeIds.includes(e.fromNodeId));
  const children = entityEdgeChildren(directChildren, edges);
  return [...currentEdges, ...children];
}

export function entityEdgeParents(
  currentEdges: I.EntityEdge[], edges: I.EntityEdge[], includeChildren?: boolean,
): I.EntityEdge[] {
  if (currentEdges.length === 0) return []; // no parent

  // Find direct parent(s)
  const currentEdgesIds = currentEdges.map((e) => `${e.fromNodeId}-${e.toNodeId}`);

  // Iterate to only return direct parents
  const currentEdgesToNodes = arrayFlat(
    currentEdges.map((ce) => edges.filter((e) => e.toNodeId === ce.fromNodeId)).filter((e) => e.length > 0),
  );
  if (!includeChildren) return [...currentEdges, ...entityEdgeParents(currentEdgesToNodes, edges, false)];

  // Find children from direct parent(s) to return a larger tree including all children from parents (siblings etc.)
  const children = entityEdgeChildren(currentEdges, edges).filter((e) => (
    !currentEdgesIds.includes(`${e.fromNodeId}-${e.toNodeId}`)
  ));
  const childrenIds = children.map((e) => `${e.fromNodeId}-${e.toNodeId}`);
  const currentAndParents = edges.filter((e) => !childrenIds.includes(`${e.fromNodeId}-${e.toNodeId}`));
  return [...currentAndParents];
}

export function trimmedNodeProperties(trimmed: boolean, node: I.EntityNode): Partial<Node> {
  return {
    borderWidth: trimmed ? 2 : 1,
    shapeProperties: {
      borderDashes: trimmed ? [4] : [],
    },
    title: trimmed
      ? I18n.utils.entityHierarchy.calcNetworkData.trimmed(node.missingChildrenCount, node.missingParentsCount)
      : undefined,
  };
}

export function highlightedNodeProperties(highlighted: boolean, nodeEntityColor: string) {
  if (highlighted) {
    return {
      borderWidth: 2,
      borderWidthSelected: 1,
      shadow: {
        enabled: true, size: 4, color: darken(nodeEntityColor, 0.5), x: 2, y: 2,
      },
      color: {
        hover: {
          background: lighten(nodeEntityColor, 0.95),
          border: nodeEntityColor,
        },
      },
    };
  }

  return {
    borderWidth: 1,
    borderWidthSelected: 1,
    shadow: {
      enabled: false,
    },
  };
}

export function isTrimmed(node: I.EntityNode) {
  return (node.missingChildrenCount || 0) > 0 || (node.missingParentsCount || 0) > 0;
}

export function addPositionsToNodes(nodes: Node[], entityNodes: I.EntityNode[], positions: NodePositionWithId[]) {
  return nodes.map((node) => {
    const position = positions.find((d) => d.id === node.id);
    const entityNode = entityNodes.find((n) => n.id === node.id) as I.EntityNode;

    return {
      ...node,
      level: undefined,
      x: position?.x,
      y: position?.y,
      hidden: position?.visibility === 'hidden' || false,
      ...trimmedNodeProperties(position?.visibility === 'trimmed' || isTrimmed(entityNode), entityNode),
    };
  });
}

function isEdgeInList(edge: I.EntityEdge, edges: I.EntityEdge[]) {
  return edges.map((e) => (
    e.fromNodeId + e.toNodeId
  )).includes(edge.fromNodeId + edge.toNodeId);
}

function isEdgeFromInList(edge: I.EntityEdge, edges: I.EntityEdge[]) {
  return edges.map((e) => (
    e.fromNodeId
  )).includes(edge.fromNodeId);
}

/**
 * Remove redundant connections to nodes
 * i.e. with nodes A, B, C where A->B, A->C, B->C. The connection A->C is unnecessary as it's implied by A->B and B->C.
 */
export function filterOutRedundantEdges(edges: I.EntityEdge[], nodes: I.EntityNode[]) {
  return edges.filter((edge) => {
    const edgesConnectingToIdenticalNode = edges.filter((e) => e.toNodeId === edge.toNodeId);
    if (edgesConnectingToIdenticalNode.length < 2) return true; // root node and nodes having just one connection

    const levelEdges = edgesConnectingToIdenticalNode.reduce<Record<number, I.EntityEdge[]>>(
      (acc, e) => {
        const node = nodes.find((n) => n.id === e.fromNodeId) as I.EntityNode;
        acc[node.level] ||= [];
        acc[node.level].push(e);
        return acc;
      }, {},
    );

    const levels = Object.keys(levelEdges).map((key) => Number(key));
    const closestLevelToNode = Math.max(...levels);
    const isEdgeInClosestLevelToNode = isEdgeInList(edge, levelEdges[closestLevelToNode]);

    if (isEdgeInClosestLevelToNode) {
      return true;
    }

    const parentEdges = entityEdgeParents(levelEdges[closestLevelToNode], edges);

    delete levelEdges[closestLevelToNode];

    let isEdgeInDifferentBranch = false;

    // Otherwise, if it's an edge in a level above but in a different sub-tree it should be kept
    // i.e. node A is level 2 and node B is level 3, don't pick the parent (B) in closest level only
    //      if parent A is not a parent of B: it might be a different branch
    arrayFlat(Object.values(levelEdges)).forEach((e) => {
      if (e.fromNodeId + e.toNodeId === edge.fromNodeId + edge.toNodeId) {
        // Check if the current edge is within the parent tree, if not keep the edge
        if (!isEdgeFromInList(e, parentEdges)) {
          isEdgeInDifferentBranch = true;
        }
      }
    });

    return isEdgeInDifferentBranch;
  });
}

export type EntityHierarchyViewId = string;

/**
 * Create a unique ID for a given entity hierarchy using the first node id and the number of nodes.
 * A check of identical ids is still needed in case a node has been removed and another one added
 * but it's enough to filter out any obvious changes.
 */
export function getUniqueId(
  entityHierarchy: I.EntityHierarchy | undefined, view: EntityHierarchyView,
): EntityHierarchyViewId | undefined {
  if (!entityHierarchy || !entityHierarchy.nodes || entityHierarchy.nodes.length < 2) return undefined;

  const { nodes } = entityHierarchy;
  const highlightedNodeId = nodes.find((n) => n.highlighted)?.id;

  return [
    // Scope 1: first item on the tree (identify the tree across pages)
    nodes[0].id,
    // Scope 2: length of the tree
    nodes.length,
    // Scope 3: type of view
    view,
    // Scope 4: when view is all the view is shared to all nodes, otherwise it's one view per node
    //          as the children/parents tree differs from one node to another
    view === EntityHierarchyView.all ? 'all' : highlightedNodeId,
  ].join('-');
}

/**
 * Calculate canvas height within the section based on available viewport size
 */
export function calcCanvasHeight(
  showAppHeaderEnv: boolean,
  nodeHeight: number,
  arrowLength: number,
  levelsCount: number,
  ignoreMaxCanvasMaxHeight?: boolean,
) {
  const canvasMaxHeight = sectionContentMaxHeight(viewportHeight(), calcPageMarginTop(showAppHeaderEnv), false);

  // Canvas height: number of levels + arrows + margin top/bottom to avoid overlapping gradient
  let canvasHeight = (levelsCount * nodeHeight) + ((levelsCount - 1) * arrowLength) + 60;
  // Ensure the canvas height is not bigger than the space available in the viewport
  if (!ignoreMaxCanvasMaxHeight && canvasHeight > canvasMaxHeight) canvasHeight = canvasMaxHeight;

  return canvasHeight;
}

export function calcNodesPerLevel(nodes: Node[]) {
  return nodes.reduce<Record<number, number>>((acc, n) => {
    if (!n.level) return acc;
    if (!acc[n.level]) acc[n.level] = 0;
    acc[n.level] += 1;
    return acc;
  }, {});
}

export function calcLevelsCount(nodes: Node[], orientation?: Orientation) {
  if (orientation === Orientation.horizontal) {
    const nodesPerLevel = calcNodesPerLevel(nodes);
    return Math.max(...Object.values(nodesPerLevel));
  }

  return arrayUnique(nodes.map((n) => n.level)).length;
}

export const fontSize = 13;

export function filterEdgesForNetworkData(
  edges: I.EntityEdge[],
  nodes: I.EntityNode[],
  view: EntityHierarchyView,
  highlightedNodeId: string | undefined,
  simplified: boolean,
) {
  let filteredEdges = edges;

  filteredEdges = filteredEdges.filter((e) => e.fromNodeId !== e.toNodeId);
  if (simplified) {
    filteredEdges = filterOutRedundantEdges(filteredEdges, nodes);
  }

  if (view === EntityHierarchyView.directAscendant) {
    filteredEdges = entityEdgeParents(
      filteredEdges.filter((e) => e.toNodeId === highlightedNodeId),
      filteredEdges,
    );
  }

  if (view === EntityHierarchyView.ascendant) {
    filteredEdges = entityEdgeParents(
      filteredEdges.filter((e) => e.toNodeId === highlightedNodeId),
      filteredEdges,
      true,
    );
  }

  if (view === EntityHierarchyView.descendant) {
    filteredEdges = entityEdgeChildren(
      filteredEdges.filter((e) => e.fromNodeId === highlightedNodeId),
      filteredEdges,
    );
  }

  return filteredEdges;
}

export function calcNetworkData(
  showAppHeaderEnv: boolean,
  entityHierarchy: I.EntityHierarchy,
  networkData: NetworkData | undefined,
  view: EntityHierarchyView,
  highlightedNodeId: string | undefined,
  viewSettings: EntityHierarchyViewSettings,
  presetCanvasHeight: number | undefined,
  force?: boolean,
) {
  const nodesPropertiesCount: Record<string, number> = {};
  const edgesPropertiesCount: Record<string, number> = {};

  const maxNodeWidth = 350;
  const iconSpacing = viewSettings.showIcons ? ThemeVars.spacing * 4.5 : 0;

  if (!networkData || force) {
    const { nodes, edges } = entityHierarchy;
    const allNodeText: string[] = [];

    const filteredEdges = filterEdgesForNetworkData(
      edges,
      nodes,
      view,
      highlightedNodeId,
      viewSettings.edges === 'simplified',
    );

    const filteredEdgesIds = arrayFlat(filteredEdges.map((e) => [e.fromNodeId, e.toNodeId]));
    let filteredNodes = nodes.filter((n) => filteredEdgesIds.includes(n.id));
    if (filteredNodes.length === 0) filteredNodes = nodes.filter((n) => n.id === highlightedNodeId);

    const isSingleEntityTypeHierarchy = entityHierarchy.entityTypes.length < 2;

    const data = {
      nodes: filteredNodes.map((node) => {
        const nodeEntityType = entityHierarchy.entityTypes.find((t) => t.id === node.typeId) as I.EntityType;
        const entitySettings = viewSettings.entitiesSettings[nodeEntityType.id];
        const nodeEntityColor = entitySettings.color;
        const nodeEntityProperties = entitySettings.properties;
        const nodeEntityInfos = nodeEntityType?.infos || [];

        let metaInfos = '';

        nodesPropertiesCount[node.id] = 0;

        nodeEntityInfos.forEach((info) => {
          if (nodeEntityProperties && !nodeEntityProperties.includes(info.id)) {
            return;
          }
          const value = node.propertyValues.find((pv) => pv.id === info.id);

          if (value && isItemWithValue(value.item)) {
            const { item } = value;
            const textValue = isMultiUnitValuesItem(item)
              ? [
                item.values.find((v) => v.unitId === item.unitId)?.value,
                item.values.find((v) => v.unitId === item.unitId)?.unitLabel,
              ].join(' ')
              : item.value;

            const text = `\n${info.label}: ${textValue}`;
            allNodeText.push(text);
            metaInfos += text;
            nodesPropertiesCount[node.id] += 1;
          }
        });

        allNodeText.push(node.label);

        let newNode: Node = {
          id: node.id,
          label: `${node.label}\n${metaInfos}`,
          labelHighlightBold: false,
          font: {
            multi: 'true',
            align: 'top',
            color: theme.palette.text.primary,
            bold: 'false',
            size: fontSize,
            face: theme.typography.fontFamily,
          },
          color: {
            background: isSingleEntityTypeHierarchy ? '#ffffff' : lighten(nodeEntityColor, 0.85),
            border: nodeEntityColor,
            hover: {
              background: lighten(nodeEntityColor, 0.95),
              border: nodeEntityColor,
            },
            highlight: {
              background: isSingleEntityTypeHierarchy ? '#ffffff' : lighten(nodeEntityColor, 0.85),
              border: nodeEntityColor,
            },
          },
          widthConstraint: { maximum: maxNodeWidth },
          level: node.level,
          margin: {
            top: ThemeVars.spacing + iconSpacing,
            right: ThemeVars.spacing,
            bottom: ThemeVars.spacing,
            left: ThemeVars.spacing,
          },
          ...trimmedNodeProperties(isTrimmed(node), node),
        };

        newNode = deepmerge(newNode, highlightedNodeProperties(node.highlighted, nodeEntityColor));

        return newNode;
      }),
      edges: filteredEdges.map((edge, i) => {
        let metaInfos = '';

        if (edge.infos) {
          edgesPropertiesCount[i] = 0;

          edge.infos.forEach((info) => {
            const value = edge.propertyValues.find((pv) => pv.id === info.id);

            if (value && isItemWithValue(value.item)) {
              const item = value.item;

              if (isMultiUnitValuesItem(item)) {
                const itemValue = item.values.find((v) => v.unitId === item.unitId);
                metaInfos += `\n${info.label}: ${itemValue?.value} ${itemValue?.unitLabel}`;
              } else {
                metaInfos += `\n${info.label}: ${value.item.value as string}`;
              }
              edgesPropertiesCount[i] += 1;
            }
          });
        }

        // Add an extra empty line so it doesn't overlap the arrow
        metaInfos += '\n ';

        const newEdge: Edge = {
          from: edge.fromNodeId,
          to: edge.toNodeId,
          label: metaInfos,
          labelHighlightBold: false,
          font: { multi: 'true' },
          width: 1,
          color: {
            color: theme.palette.text.primary,
            hover: theme.palette.text.primary,
          },
          hoverWidth: 1,
        };

        if (newEdge.from === highlightedNodeId) {
          newEdge.width = 2;
          newEdge.color = {
            color: theme.palette.primary.main,
            hover: theme.palette.primary.main,
          };
        }

        return newEdge;
      }),
    };

    data.edges = data.edges.map((edge) => {
      const newEdge: Edge = { ...edge };

      const fromNode = data.nodes.find((n) => n.id === edge.from);
      const toNode = data.nodes.find((n) => n.id === edge.to);

      if (toNode?.level === undefined || fromNode?.level === undefined) return edge;

      const levelsDiff = Math.abs(toNode.level - fromNode.level);

      // Curve edge when an arrow is going through more than one level
      if (levelsDiff > 1) {
        newEdge.smooth = {
          enabled: true,
          type: 'curvedCW',
          forceDirection: true,
          roundness: 0.05 + (levelsDiff * 0.05),
        };
      }

      return newEdge;
    });

    const font = `regular ${fontSize}px ${theme.typography.fontFamily as string}`;
    const longestNodeTextLineWidth = Math.max(...allNodeText.map((line) => (
      getTextWidth(line, font, theme.typography.body2.letterSpacing as string)
    )));
    const maxNodePropsNo = Math.max(...Object.values(nodesPropertiesCount));
    const maxEdgePropsNo = Math.max(...Object.values(edgesPropertiesCount));
    const levelsCount = calcLevelsCount(data.nodes, viewSettings?.orientation);

    const textLineHeight = fontSize;
    const margin = ThemeVars.spacing;
    const nodeBorderWidth = 2;

    // Node content: ((number of lines of text + label + empty line) * line height)
    const nodeTextHeight = ((maxNodePropsNo + 2) * textLineHeight);
    // Node: node content + top/bottom margin + icon margin
    const nodeHeight = nodeTextHeight + (margin * 2) + iconSpacing;
    // Arrow: ((number of lines of text + empty lines) * line height)
    let arrowLength = ((maxEdgePropsNo + 4) * textLineHeight);
    if (arrowLength < 60) arrowLength = 60;
    // Node + Arrow
    const maxBlockHeight = arrowLength + nodeHeight;
    // Canvas height
    let canvasHeight = presetCanvasHeight === undefined
      ? calcCanvasHeight(showAppHeaderEnv, nodeHeight, arrowLength, levelsCount)
      : presetCanvasHeight;
    if (presetCanvasHeight === undefined && viewSettings?.orientation === Orientation.horizontal) canvasHeight += 37; // ensure there is no overlap with the unlock button
    // Max number of visible levels
    const canvasMaxVisibleLevels = Math.floor((canvasHeight + arrowLength) / maxBlockHeight);
    const widestNodeWidth = longestNodeTextLineWidth + margin + margin + nodeBorderWidth + nodeBorderWidth;
    const adjustedWidestNodeWidth = widestNodeWidth > maxNodeWidth ? maxNodeWidth : widestNodeWidth;

    let hierarchicalOptions = {
      direction: 'UD',
      levelSeparation: maxBlockHeight, // vertical separation
      nodeSpacing: adjustedWidestNodeWidth, // horizontal separation
    };

    if (viewSettings?.orientation === Orientation.horizontal) {
      hierarchicalOptions = {
        direction: 'LR',
        levelSeparation: adjustedWidestNodeWidth,
        nodeSpacing: maxBlockHeight,
      };
    }

    const options: Options = {
      interaction: { hover: true },
      height: `${canvasHeight}px`,
      layout: {
        hierarchical: {
          sortMethod: 'directed',
          ...hierarchicalOptions,
        },
      },
      nodes: {
        shape: 'box',
        margin,
        heightConstraint: {
          // this variable is missing in TS types
          // only the content as it adds up with the node margins
          minimum: nodeTextHeight,
        },
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } as any,
      edges: {
        arrows: 'to',
        length: arrowLength,
      },
      physics: false,
    };

    /** Calc number of nodes horizontally and vertically to define the size of our canvas when we wish to render it all at scale 1 */
    const calcFullSize = () => {
      const nodesPerLevel = calcNodesPerLevel(data.nodes);

      const nodesLevels = Object.keys(nodesPerLevel).length;
      const maxNodesPerLevel = nodesPerLevel[
        (Object.keys(nodesPerLevel) as unknown as number[]).sort((a, b) => nodesPerLevel[b] - nodesPerLevel[a])[0]
      ];

      const fullSize: NetworkData['fullSize'] = {
        width: adjustedWidestNodeWidth * maxNodesPerLevel,
        height: calcCanvasHeight(showAppHeaderEnv, nodeHeight, arrowLength, nodesLevels, true),
      };

      return fullSize;
    };

    const newNetworkData: NetworkData = {
      data,
      options,
      canvasMaxVisibleLevels,
      nodes,
      edges,
      fullSize: calcFullSize(),
      nodeWidth: adjustedWidestNodeWidth,
      nodeHeight,
      arrowLength,
      levelsCount,
    };

    return newNetworkData;
  }

  return undefined;
}

/**
 * Set an identical color to prev/next settings and then compare if anything else has changed
 */
export function hasOnlyViewSettingsColorsBeenChanged(
  prevViewSettings: EntityHierarchyViewSettings,
  nextViewSettings: EntityHierarchyViewSettings,
) {
  const prevViewSettingsWithoutColors: EntityHierarchyViewSettings = {
    ...prevViewSettings,
    entitiesSettings: Object.keys(
      prevViewSettings.entitiesSettings,
    ).reduce<EntityHierarchyViewSettings['entitiesSettings']>((acc, entityId) => {
      acc[entityId] = { ...prevViewSettings.entitiesSettings[entityId], color: '#ffffff' };
      return acc;
    }, {}),
  };

  const nextViewSettingsWithoutColors: EntityHierarchyViewSettings = {
    ...nextViewSettings,
    entitiesSettings: Object.keys(
      nextViewSettings.entitiesSettings,
    ).reduce<EntityHierarchyViewSettings['entitiesSettings']>((acc, entityId) => {
      acc[entityId] = { ...nextViewSettings.entitiesSettings[entityId], color: '#ffffff' };
      return acc;
    }, {}),
  };

  const hasOnlyColorsBeenChanged = isEqual(prevViewSettingsWithoutColors, nextViewSettingsWithoutColors);

  return hasOnlyColorsBeenChanged;
}
